import React, { useEffect, useState } from "react";

const INITIAL_FORM = {
  name: "",
  categoryId: "",
  type: "",
  shortDescription: "",
  description: "",
  displayOrder: 0,
  isActive: true,
};

const OfferingForm = ({
  initialData = null,
  categories = [],
  loading = false,
  submitting = false,
  error = null,
  submitLabel = "Save Offering",
  onSubmit,
  onCancel,
}) => {
  const [form, setForm] = useState(
    INITIAL_FORM
  );

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!initialData) {
      setForm(INITIAL_FORM);
      return;
    }

    setForm({
      name: initialData.name || "",
      categoryId:
        initialData.category_id || "",
      type: initialData.type || "",
      shortDescription:
        initialData.short_description || "",
      description:
        initialData.description || "",
      displayOrder:
        initialData.display_order ?? 0,
      isActive:
        initialData.is_active ?? true,
    });
  }, [initialData]);

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: undefined,
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (
      !form.name ||
      form.name.trim().length < 2
    ) {
      nextErrors.name =
        "Offering name must be at least 2 characters";
    }

    if (!form.categoryId) {
      nextErrors.categoryId =
        "Category is required";
    }

    if (!form.type) {
      nextErrors.type =
        "Offering type is required";
    }

    if (
      form.shortDescription &&
      form.shortDescription.length > 500
    ) {
      nextErrors.shortDescription =
        "Short description cannot exceed 500 characters";
    }

    if (
      !Number.isInteger(
        Number(form.displayOrder)
      ) ||
      Number(form.displayOrder) < 0
    ) {
      nextErrors.displayOrder =
        "Display order must be a non-negative integer";
    }

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors).length === 0
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      name: form.name.trim(),

      categoryId: form.categoryId,

      type: form.type,

      shortDescription:
        form.shortDescription.trim() || null,

      description:
        form.description.trim() || null,

      displayOrder: Number(
        form.displayOrder
      ),

      ...(initialData
        ? {
            isActive: form.isActive,
          }
        : {}),
    };

    await onSubmit?.(payload);
  };

  if (loading) {
    return (
      <div className="offering-form-state">
        Loading offering...
      </div>
    );
  }

  return (
    <form
      className="offering-form"
      onSubmit={handleSubmit}
    >
      {error && (
        <div className="form-error">
          {error}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="name">
          Offering Name *
        </label>

        <input
          id="name"
          name="name"
          type="text"
          value={form.name}
          onChange={handleChange}
          placeholder="Enter offering name"
          disabled={submitting}
        />

        {errors.name && (
          <span className="field-error">
            {errors.name}
          </span>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="categoryId">
            Category *
          </label>

          <select
            id="categoryId"
            name="categoryId"
            value={form.categoryId}
            onChange={handleChange}
            disabled={submitting}
          >
            <option value="">
              Select category
            </option>

            {categories.map((category) => (
              <option
                key={category.id}
                value={category.id}
              >
                {category.name}
              </option>
            ))}
          </select>

          {errors.categoryId && (
            <span className="field-error">
              {errors.categoryId}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="type">
            Offering Type *
          </label>

          <select
            id="type"
            name="type"
            value={form.type}
            onChange={handleChange}
            disabled={submitting}
          >
            <option value="">
              Select type
            </option>

            <option value="PRODUCT">
              Product
            </option>

            <option value="SERVICE">
              Service
            </option>

            <option value="SOLUTION">
              Solution
            </option>
          </select>

          {errors.type && (
            <span className="field-error">
              {errors.type}
            </span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="shortDescription">
          Short Description
        </label>

        <textarea
          id="shortDescription"
          name="shortDescription"
          value={form.shortDescription}
          onChange={handleChange}
          maxLength={500}
          rows={3}
          placeholder="Short description"
          disabled={submitting}
        />

        <div className="field-counter">
          {form.shortDescription.length}/500
        </div>

        {errors.shortDescription && (
          <span className="field-error">
            {errors.shortDescription}
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="description">
          Description
        </label>

        <textarea
          id="description"
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={7}
          placeholder="Detailed offering description"
          disabled={submitting}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="displayOrder">
            Display Order
          </label>

          <input
            id="displayOrder"
            name="displayOrder"
            type="number"
            min="0"
            value={form.displayOrder}
            onChange={handleChange}
            disabled={submitting}
          />

          {errors.displayOrder && (
            <span className="field-error">
              {errors.displayOrder}
            </span>
          )}
        </div>

        {initialData && (
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                disabled={submitting}
              />

              <span>
                Active Offering
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="offering-form-actions">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={submitting}
        >
          {submitting
            ? "Saving..."
            : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default OfferingForm;